import React, { useState } from 'react';
import { Button, Icon } from 'semantic-ui-react';
import EligibilityProductSelector from '../../components/EligibilityProductSelector';
import {
  useEligibilityTheme,
  useHasUnsavedChanges,
  useEligibilityActions,
} from '../../hooks/useEligibilityEngine';
import StatusHistoryModal from './components/StatusHistoryModal';
import RevertConfirmationModal from './components/RevertConfirmationModal';
import PublishModal from './components/PublishModal';
import './actionPanel.css';

const ActionPanel = () => {
  const { productType, selectedProductData, selectedProduct } = useEligibilityTheme();
  const hasUnsavedChanges = useHasUnsavedChanges();
  const { onRevert, onPublish, fetchRules } = useEligibilityActions();

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [publishError, setPublishError] = useState('');

  const handleViewHistory = () => {
    setShowHistoryModal(true);
  };

  const handleCloseHistory = () => {
    setShowHistoryModal(false);
  };

  const handleRevertClick = () => {
    setShowRevertModal(true);
  };

  const handleCloseRevertModal = () => {
    setShowRevertModal(false);
  };

  const handleConfirmRevert = () => {
    setShowRevertModal(false);
    onRevert();
  };

  const handleOpenPublishModal = () => {
    setPublishError('');
    setShowPublishModal(true);
  };

  const handleClosePublishModal = () => {
    setShowPublishModal(false);
    setPublishError('');
  };

  const handlePublishConfirm = async effectiveFrom => {
    setPublishLoading(true);
    setPublishError('');
    const result = await onPublish({ effectiveFrom });
    setPublishLoading(false);
    if (result.success) {
      setShowPublishModal(false);
      fetchRules(selectedProduct);
    } else {
      setPublishError(
        result.errorMessage || 'Failed to publish. Please try again.'
      );
    }
  };

  return (
    <>
      <div className='action-panel' data-testid='action-panel'>
        <div className='ui container action-panel-container'>
          <div className='action-panel-left'>
            <EligibilityProductSelector />
          </div>
          <div className='action-panel-right'>
            {hasUnsavedChanges && (
              <Button
                className='action-button unsaved'
                data-testid='unsaved-changes-button'
              >
                <Icon name='warning sign' />
                Unsaved changes
              </Button>
            )}
            <Button
              className='action-button secondary'
              onClick={handleRevertClick}
              disabled={!hasUnsavedChanges}
              data-testid='revert-button'
            >
              <Icon name='undo' />
            </Button>
            <Button
              className='action-button secondary'
              onClick={handleViewHistory}
              data-testid='view-history-button'
            >
              <Icon name='history' />
              View History
            </Button>
            <Button
              className='action-button secondary'
              onClick={handleOpenPublishModal}
              disabled={!hasUnsavedChanges}
              data-testid='publish-button'
            >
              <Icon name='upload' />
              Publish
            </Button>
          </div>
        </div>
      </div>
      <StatusHistoryModal
        open={showHistoryModal}
        onClose={handleCloseHistory}
        fieldLabel={selectedProductData?.displayName || 'Product'}
        productCode={selectedProduct}
      />
      <PublishModal
        open={showPublishModal}
        onClose={handleClosePublishModal}
        onConfirm={handlePublishConfirm}
        loading={publishLoading}
        error={publishError}
        productName={selectedProductData?.displayName || 'Product'}
        productType={productType}
      />
      <RevertConfirmationModal
        open={showRevertModal}
        onClose={handleCloseRevertModal}
        onConfirm={handleConfirmRevert}
      />
    </>
  );
};

export default ActionPanel;
